import { Injectable, inject, signal } from '@angular/core';
import { GeoJSONSource, LngLatBounds, LngLatLike, Map } from 'maplibre-gl';
import { LatLng } from '../../models/latlng';
import { MapSkin } from '../account/settings.service';
import { Waypoint } from '../../models/range-params';
import { Incident, RoadSurfaceAlert } from '../../models/route';
import { scaleRaster } from './map-symbol-scale';
import { MapPaletteService } from './map-palette.service';

// Route fitting
const ROUTE_PADDING_BASE = 100;
// Share of the map that has to stay free for the route when the paddings
// (overlay panels included) would otherwise take up the whole canvas.
const MIN_ROUTE_SPAN_RATIO = 0.2;
// Whitespace left between the route (its markers included) and the floating UI.
const ROUTE_OVERLAY_GAP = 40;
// How far route symbols reach past the fitted bounds. Above the last route
// point sits the destination pin's battery badge (offset 56px up, ~24px tall);
// below the current position the cursor disc is centred, so half of it (the
// 125px raster is drawn at half size) hangs under the first route point.
const ROUTE_MARKER_OVERHANG_TOP = 80;
const ROUTE_CURSOR_OVERHANG_BOTTOM = 40;
// Floating UI that overlaps the map in the portrait layout, measured live
// because both the panel (drag-resizable) and the bottom bar can change height.
const TOP_OVERLAY_SELECTORS = ['.planning-panel'];
const BOTTOM_OVERLAY_SELECTORS = ['#wind-panel', '#voice-icon', '#slider-icon'];
// Breakpoint values — keep in sync with breakpoints.scss
const COMPACT_LAYOUT_MAX_WIDTH = 1050;
const COMPACT_LAYOUT_MAX_HEIGHT = 700;

// Map cards (POI card, charging popup)
// Top overlays that stay on screen while a card is open. The planning panel is
// not among them: on the compact layout it hides itself while a card is open
// (see PlanningPanelComponent.hiddenForMapCard), because the panel and a
// charging popup together are often taller than the screen.
const CARD_TOP_OVERLAY_SELECTORS = ['.header-panel .header-top'];
// Whitespace kept between a card and the floating UI when the card is taller
// than the free map area and can only be aligned to the top of it.
const CARD_OVERLAY_GAP = 8;
// Below this the strip between the panels is too small to be worth targeting;
// the card is then centred on the whole canvas as if nothing covered it.
const MIN_FREE_AREA_HEIGHT = 120;
// Incident and road surface alert icons are skin independent, one set serves both map skins.
const INCIDENT_ICON_BASE = 'assets/icons/default/map/incidents';
const ROAD_SURFACE_ALERT_ICON_BASE = 'assets/icons/default/map/road_surface_alerts';

interface MapPadding { top: number; bottom: number; left: number; right: number; }

const CHARGER_GROUP_SIZES = [45, 50, 60, 70];
const CHARGER_SIZES = [34, 44, 54, 60];
const CHARGER_GROUP_COLORS = [
  { color: "#FBBF24", skin: MapSkin.Dark },
  { color: "#34D399", skin: MapSkin.Dark },
  { color: "#38BDF8", skin: MapSkin.Dark },
  { color: "#D97706", skin: MapSkin.Light },
  { color: "#059669", skin: MapSkin.Light },
  { color: "#0284C7", skin: MapSkin.Light }
];

@Injectable({
  providedIn: 'root'
})

export class MapDrawerHelperService {

  private offscreenCanvas;
  private mapInstance: Map | null = null;
  private routePlanningAnimationId: number | null = null;

  /** Cards (POI card, charging popup) currently attached to the map. */
  private readonly openCards = new Set<HTMLElement>();
  /** True while at least one map card is open; the portrait planning panel hides on it. */
  public readonly mapCardOpen = signal<boolean>(false);

  // Icon URL definitions for MapLibre symbol layers
  public readonly iconUrls = {
    endDark: '../assets/icons/dark/map/route_point_target.svg?v=2',
    endLight: '../assets/icons/light/map/route_point_target.svg?v=2',
    endFailedDark: '../assets/icons/dark/map/route_point_target_failed.svg?v=2',
    endFailedLight: '../assets/icons/light/map/route_point_target_failed.svg?v=2'
  };

  private readonly mapPaletteService = inject(MapPaletteService);

  constructor() {
    this.initChargerIcons();
  }

  public setMapInstance(map: Map): void {
    this.mapInstance = map;
  }

  public addContextMenuCircle(latlng: LatLng, skin: MapSkin): void {
    if (!this.mapInstance) return;
    this.removeContextMenuCircle();

    this.mapInstance.addSource('context-menu-circle', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Point', coordinates: [latlng.lng, latlng.lat] }
      }
    });

    this.mapInstance.addLayer({
      id: 'context-menu-circle',
      type: 'circle',
      source: 'context-menu-circle',
      paint: {
        'circle-radius': 25,
        'circle-color': skin === MapSkin.Light ? '#fff' : 'transparent',
        'circle-opacity': skin === MapSkin.Light ? 0.7 : 0,
        'circle-stroke-color': skin === MapSkin.Light
          ? this.mapPaletteService.accentLight('#7ed3f3', 0.35)
          : this.mapPaletteService.accentLight('#81d4fa', 0.45),
        'circle-stroke-width': 2
      }
    });
  }

  public removeContextMenuCircle(): void {
    if (!this.mapInstance) return;
    if (this.mapInstance.getLayer('context-menu-circle')) {
      this.mapInstance.removeLayer('context-menu-circle');
    }
    if (this.mapInstance.getSource('context-menu-circle')) {
      this.mapInstance.removeSource('context-menu-circle');
    }
  }

  public addEndMarker(skin: MapSkin, endPos: LatLng, failedPlan: boolean): void {
    if (!this.mapInstance) return;
    const imageId = 'end-marker-icon';
    let iconUrl: string;
    if (skin === MapSkin.Light && !failedPlan) {
      iconUrl = this.iconUrls.endLight;
    } else if (skin === MapSkin.Light && failedPlan) {
      iconUrl = this.iconUrls.endFailedLight;
    } else if (!failedPlan) {
      iconUrl = this.iconUrls.endDark;
    } else {
      iconUrl = this.iconUrls.endFailedDark;
    }
    this.addSymbolMarker('end-marker', imageId, iconUrl, endPos, 'bottom');
  }

  public removeStartEndMarkers(): void {
    this.removeSymbolMarker('start-marker', 'start-marker-icon');
    this.removeSymbolMarker('end-marker', 'end-marker-icon');
  }

  private addSymbolMarker(layerId: string, imageId: string, iconUrl: string, position: LatLng, anchor: string): void {
    if (!this.mapInstance) return;

    // Remove existing
    if (this.mapInstance.getLayer(layerId)) {
      this.mapInstance.removeLayer(layerId);
    }
    if (this.mapInstance.getSource(layerId)) {
      this.mapInstance.removeSource(layerId);
    }
    if (this.mapInstance.hasImage(imageId)) {
      this.mapInstance.removeImage(imageId);
    }

    const img = new Image(scaleRaster(42), scaleRaster(51));
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (!this.mapInstance) return;
      if (!this.mapInstance.hasImage(imageId)) {
        this.mapInstance.addImage(imageId, img);
      }

      this.mapInstance.addSource(layerId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: [position.lng, position.lat] }
        }
      });

      this.mapInstance.addLayer({
        id: layerId,
        type: 'symbol',
        source: layerId,
        layout: {
          'icon-image': imageId,
          'icon-size': 1,
          'icon-anchor': anchor as any,
          'icon-allow-overlap': true
        }
      });
    };
    img.src = iconUrl;
  }

  private removeSymbolMarker(layerId: string, imageId: string): void {
    if (!this.mapInstance) return;
    if (this.mapInstance.getLayer(layerId)) {
      this.mapInstance.removeLayer(layerId);
    }
    if (this.mapInstance.getSource(layerId)) {
      this.mapInstance.removeSource(layerId);
    }
  }

  public getChargerHtmlElements(skin: MapSkin, statusType: string, statusTypeText: string, statusStateText: string): string {
    return `<img class="status-icon" src="/assets/icons/${skin}/map/status_icon_${statusType}.svg"/>
    <div class="status-state">${statusStateText ? "<p>" + statusStateText + "</p>" : ""}<p class="state-type">${statusTypeText}</p></div>`;
  }

  public addRoutePlanningAnimation(waypoints: LatLng[], skin: MapSkin): void {
    if (!this.mapInstance || waypoints.length < 2) return;
    this.removeRoutePlanningAnimation();

    const coordinates: [number, number][] = waypoints.map(w => [w.lng, w.lat]);
    const isLight = skin === MapSkin.Light;
    const palette = this.mapPaletteService;
    const primaryColor = isLight ? palette.accent('#059cdb') : palette.accentLight('#81d4fa', 0.45);
    const glowColor = palette.accentAlpha(isLight ? 'rgba(5,156,219,0.12)' : 'rgba(79,195,247,0.12)', 0.12);
    const pulseColor = isLight ? palette.accent('#0288d1') : palette.accentLight('#b3e5fc', 0.7);

    // ── Static layers ──────────────────────────────

    this.mapInstance.addSource('route-planning-line', {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates } }
    });

    // Soft glow behind the path
    this.mapInstance.addLayer({
      id: 'route-planning-glow',
      type: 'line',
      source: 'route-planning-line',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': glowColor, 'line-width': 16, 'line-blur': 8 }
    });

    // Dashed base path (static – no animation)
    this.mapInstance.addLayer({
      id: 'route-planning-track',
      type: 'line',
      source: 'route-planning-line',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': primaryColor,
        'line-width': 3,
        'line-opacity': 0.35,
        'line-dasharray': [2, 3]
      }
    });

    // ── Animated pulse dots ────────────────────────

    // Pre-compute cumulative segment distances for interpolation
    const segDist: number[] = [0];
    for (let i = 1; i < coordinates.length; i++) {
      const dx = coordinates[i][0] - coordinates[i - 1][0];
      const dy = coordinates[i][1] - coordinates[i - 1][1];
      segDist.push(segDist[i - 1] + Math.sqrt(dx * dx + dy * dy));
    }
    const totalDist = segDist[segDist.length - 1];

    // Interpolate a position along the polyline at fraction t ∈ [0,1]
    const interpolate = (t: number): [number, number] => {
      const target = t * totalDist;
      for (let i = 1; i < segDist.length; i++) {
        if (target <= segDist[i]) {
          const segLen = segDist[i] - segDist[i - 1];
          const f = segLen === 0 ? 0 : (target - segDist[i - 1]) / segLen;
          return [
            coordinates[i - 1][0] + f * (coordinates[i][0] - coordinates[i - 1][0]),
            coordinates[i - 1][1] + f * (coordinates[i][1] - coordinates[i - 1][1])
          ];
        }
      }
      return coordinates[coordinates.length - 1];
    };

    // Create N evenly-spaced pulse dots
    const NUM_PULSES = 3;
    const initialFeatures: GeoJSON.Feature[] = [];
    for (let i = 0; i < NUM_PULSES; i++) {
      const pos = interpolate(i / NUM_PULSES);
      initialFeatures.push({
        type: 'Feature',
        properties: {},
        geometry: { type: 'Point', coordinates: pos }
      });
    }

    this.mapInstance.addSource('route-planning-pulses', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: initialFeatures }
    });

    // Outer pulse halo
    this.mapInstance.addLayer({
      id: 'route-planning-pulse-glow',
      type: 'circle',
      source: 'route-planning-pulses',
      paint: {
        'circle-radius': 10,
        'circle-color': primaryColor,
        'circle-opacity': 0.2,
        'circle-blur': 0.6
      }
    });

    // Inner bright dot
    this.mapInstance.addLayer({
      id: 'route-planning-pulse-dot',
      type: 'circle',
      source: 'route-planning-pulses',
      paint: {
        'circle-radius': 4,
        'circle-color': pulseColor,
        'circle-opacity': 0.9
      }
    });

    // ── Animation loop ─────────────────────────────

    const CYCLE_DURATION = 3000; // ms for one full traversal
    let startTime: number | null = null;

    const animate = (timestamp: number) => {
      if (!this.mapInstance || !this.mapInstance.getSource('route-planning-pulses')) {
        this.routePlanningAnimationId = null;
        return;
      }

      if (startTime === null) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = (elapsed % CYCLE_DURATION) / CYCLE_DURATION;

      const features: GeoJSON.Feature[] = [];
      for (let i = 0; i < NUM_PULSES; i++) {
        const t = (progress + i / NUM_PULSES) % 1;
        const pos = interpolate(t);
        features.push({
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: pos }
        });
      }

      (this.mapInstance.getSource('route-planning-pulses') as GeoJSONSource)
        .setData({ type: 'FeatureCollection', features });

      this.routePlanningAnimationId = requestAnimationFrame(animate);
    };

    this.routePlanningAnimationId = requestAnimationFrame(animate);

    // Fit map to show all waypoints
    const bounds = new LngLatBounds();
    coordinates.forEach(coord => bounds.extend(coord));
    if (!bounds.isEmpty()) {
      this.mapInstance.fitBounds(bounds, { padding: this.getRoutePadding(), animate: true });
    }
  }

  /**
   * Padding used to fit the map to a drawn/planned route. Base is 100px on
   * every side, plus extra room to keep the route clear of the layout's
   * overlay panels — mirrors the breakpoints in breakpoints.scss:
   *   - wide-layout: +330px left (side panel)
   *   - compact-layout-landscape: +330px left (side panel)
   *   - compact-layout-portrait: the floating UI is measured, because the
   *     planning panel is drag-resizable — a fixed value leaves the route
   *     squeezed against the bottom edge instead of centred in the free map
   *     area, and leaves the destination badge crowding the panel while the
   *     cursor disappears behind the wind panel.
   */
  public getRoutePadding(): MapPadding {
    const padding: MapPadding = {
      top: ROUTE_PADDING_BASE, bottom: ROUTE_PADDING_BASE,
      left: ROUTE_PADDING_BASE, right: ROUTE_PADDING_BASE
    };

    if (!this.isCompactPortrait()) {
      // wide-layout / compact-layout-landscape
      padding.left += 330;
    } else {
      // compact-layout-portrait
      padding.top = Math.max(padding.top,
        this.getOverlayInset(TOP_OVERLAY_SELECTORS, 'top')
        + ROUTE_OVERLAY_GAP + scaleRaster(ROUTE_MARKER_OVERHANG_TOP));
      padding.bottom = Math.max(padding.bottom,
        this.getOverlayInset(BOTTOM_OVERLAY_SELECTORS, 'bottom')
        + ROUTE_OVERLAY_GAP + scaleRaster(ROUTE_CURSOR_OVERHANG_BOTTOM));
    }

    return this.clampPaddingToMap(padding);
  }

  /** Registers a card attached to the map; pair with cardClosed() on removal. */
  public cardOpened(cardEl: HTMLElement): void {
    this.openCards.add(cardEl);
    this.mapCardOpen.set(true);
  }

  public cardClosed(cardEl: HTMLElement): void {
    this.openCards.delete(cardEl);
    this.mapCardOpen.set(this.openCards.size > 0);
  }

  /**
   * Pans the map so a card drawn inside the map container (the POI card, a
   * charging station popup) is visible in the part of the map not covered by
   * the floating UI. In the portrait layout the planning panel covers the top
   * of the canvas and the bottom bar its bottom, so centring the card on the
   * whole canvas leaves its top under the panel. The card is centred in the
   * free strip instead; a card taller than that strip is aligned to the top
   * of it so its header stays readable.
   *
   * `cardEl` must already be attached to the map at `lngLat`. Its box is
   * measured relative to the anchor's screen position, so the result does not
   * depend on where the camera happens to be when this is called, even
   * mid-flight. `shiftX` moves the card off the horizontal centre (positive =
   * right), for layouts where a side panel covers the left of the map.
   */
  public revealCard(lngLat: LngLatLike, cardEl: HTMLElement,
    options: { shiftX?: number; duration?: number } = {}): void {
    // Next frame: the card's content has been laid out and the marker placed.
    requestAnimationFrame(() => {
      const map = this.mapInstance;
      if (!map) return;

      const mapRect = map.getContainer().getBoundingClientRect();
      const cardRect = cardEl.getBoundingClientRect();
      if (cardRect.height === 0 || mapRect.height === 0) return;
      const anchor = map.project(lngLat);

      // The card's box relative to its anchor stays fixed while the map moves.
      const cardTopFromAnchor = cardRect.top - mapRect.top - anchor.y;
      const cardCenterXFromAnchor = cardRect.left + cardRect.width / 2 - mapRect.left - anchor.x;

      const free = this.getFreeVerticalArea(mapRect.height, CARD_TOP_OVERLAY_SELECTORS);
      const freeHeight = free.bottom - free.top;
      const cardTop = cardRect.height + 2 * CARD_OVERLAY_GAP <= freeHeight
        ? free.top + (freeHeight - cardRect.height) / 2
        : free.top + CARD_OVERLAY_GAP;

      // Screen point the anchor has to land on for the card to sit there.
      const anchorX = mapRect.width / 2 + (options.shiftX ?? 0) - cardCenterXFromAnchor;
      const anchorY = cardTop - cardTopFromAnchor;

      map.easeTo({
        center: lngLat,
        offset: [anchorX - mapRect.width / 2, anchorY - mapRect.height / 2],
        duration: options.duration ?? 500
      });
    });
  }

  /**
   * The vertical strip of the map not hidden by the floating UI, in container
   * pixels. Only the portrait layout stacks panels over the map; elsewhere,
   * and when the panels would leave no usable strip, the whole height is free.
   */
  private getFreeVerticalArea(mapHeight: number, topSelectors: string[]): { top: number; bottom: number } {
    const whole = { top: 0, bottom: mapHeight };
    if (!this.isCompactPortrait()) return whole;

    const area = {
      top: this.getOverlayInset(topSelectors, 'top'),
      bottom: mapHeight - this.getOverlayInset(BOTTOM_OVERLAY_SELECTORS, 'bottom')
    };
    return area.bottom - area.top >= MIN_FREE_AREA_HEIGHT ? area : whole;
  }

  /** The portrait phone layout: panels stack above and below the map. */
  private isCompactPortrait(): boolean {
    const isCompact = window.matchMedia(
      `(max-width: ${COMPACT_LAYOUT_MAX_WIDTH}px), (max-height: ${COMPACT_LAYOUT_MAX_HEIGHT}px)`
    ).matches;
    const isLandscape = window.matchMedia('(orientation: landscape)').matches;
    return isCompact && !isLandscape;
  }

  /**
   * How deep the floating UI reaches into the map from the given edge, in CSS
   * pixels. Elements that are missing or hidden contribute nothing, so an
   * unobstructed edge keeps the base padding.
   */
  private getOverlayInset(selectors: string[], edge: 'top' | 'bottom'): number {
    const container = this.mapInstance?.getContainer();
    if (!container) return 0;
    const mapRect = container.getBoundingClientRect();

    return selectors.reduce((inset, selector) => {
      const element = document.querySelector<HTMLElement>(selector);
      if (!element) return inset;

      const rect = element.getBoundingClientRect();
      if (rect.height === 0) return inset;

      const overlap = edge === 'top' ? rect.bottom - mapRect.top : mapRect.bottom - rect.top;
      return Math.max(inset, overlap);
    }, 0);
  }

  /**
   * fitBounds refuses to move the camera when the padding does not fit the
   * canvas ("Map cannot fit within canvas with the given bounds, padding
   * and/or offset"). On short viewports the panel overlap alone can get close
   * to that limit, so shrink the paddings proportionally and always leave a
   * strip of map for the route itself.
   */
  private clampPaddingToMap(padding: MapPadding): MapPadding {
    const container = this.mapInstance?.getContainer();
    if (!container) return padding;

    const { width, height } = container.getBoundingClientRect();
    this.shrinkPaddingAxis(padding, 'top', 'bottom', height);
    this.shrinkPaddingAxis(padding, 'left', 'right', width);
    return padding;
  }

  private shrinkPaddingAxis(padding: MapPadding, startSide: keyof MapPadding,
    endSide: keyof MapPadding, size: number): void {
    const minSpan = Math.max(60, size * MIN_ROUTE_SPAN_RATIO);
    const total = padding[startSide] + padding[endSide];
    if (total + minSpan <= size) return;

    const scale = Math.max(0, (size - minSpan) / total);
    padding[startSide] = Math.round(padding[startSide] * scale);
    padding[endSide] = Math.round(padding[endSide] * scale);
  }

  public removeRoutePlanningAnimation(): void {
    if (this.routePlanningAnimationId !== null) {
      cancelAnimationFrame(this.routePlanningAnimationId);
      this.routePlanningAnimationId = null;
    }
    if (!this.mapInstance) return;
    const layers = [
      'route-planning-pulse-dot', 'route-planning-pulse-glow',
      'route-planning-track', 'route-planning-glow'
    ];
    const sources = ['route-planning-pulses', 'route-planning-line'];
    layers.forEach(id => { if (this.mapInstance!.getLayer(id)) this.mapInstance!.removeLayer(id); });
    sources.forEach(id => { if (this.mapInstance!.getSource(id)) this.mapInstance!.removeSource(id); });
  }

  public addFailedRouteLine(waypoints: Waypoint[], blueColor: boolean): void {
    if (!this.mapInstance || waypoints.length < 2) return;
    this.removeFailedRouteLine();

    const coordinates = waypoints.map(w => [w.lng, w.lat]);

    this.mapInstance.addSource('failed-route-line', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates }
      }
    });

    this.mapInstance.addLayer({
      id: 'failed-route-line',
      type: 'line',
      source: 'failed-route-line',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': blueColor ? this.mapPaletteService.accent('#059cdb') : '#e0315d',
        'line-width': 6,
        'line-dasharray': [2, 2]
      }
    });
  }

  public removeFailedRouteLine(): void {
    if (!this.mapInstance) return;
    if (this.mapInstance.getLayer('failed-route-line')) {
      this.mapInstance.removeLayer('failed-route-line');
    }
    if (this.mapInstance.getSource('failed-route-line')) {
      this.mapInstance.removeSource('failed-route-line');
    }
  }

  public initChargerIcons() {
    this.offscreenCanvas = document.createElement('canvas');
    this.offscreenCanvas.width = CHARGER_GROUP_SIZES[CHARGER_GROUP_SIZES.length - 1] * CHARGER_GROUP_COLORS.length;
    this.offscreenCanvas.height = this.getArraySum(CHARGER_GROUP_SIZES);
    const offscreenCtx = this.offscreenCanvas.getContext('2d');

    CHARGER_GROUP_COLORS.forEach((color, index) => {
      CHARGER_GROUP_SIZES.forEach((size, sizeIndex) => {
        this.drawChargerIcon(offscreenCtx, size * index + (size / 2),
          this.getArraySum(CHARGER_GROUP_SIZES.slice(0, sizeIndex)) + CHARGER_GROUP_SIZES[sizeIndex] / 2, size / 2, color.color, color.skin);
      });
    });
  }

  private getArraySum(array: number[]) {
    return array.reduce((accumulator, currentValue) => { return accumulator + currentValue }, 0);
  }

  private drawChargerIcon(ctx, cx, cy, radius, mainColor, skin: MapSkin) {
    ctx.beginPath();
    ctx.fillStyle = mainColor;
    ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
    ctx.globalAlpha = 0.4;
    ctx.fill();

    if (skin == MapSkin.Light) {
      ctx.beginPath();
      ctx.arc(cx, cy, radius * 0.64, 0, 2 * Math.PI);
      ctx.globalAlpha = 1;
      ctx.fillStyle = "#A1A1A1";
      ctx.fill();
    }

    ctx.beginPath();
    ctx.arc(cx, cy, radius * 0.61, 0, 2 * Math.PI);
    ctx.fillStyle = "#FFF";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 0.64, 0, 2 * Math.PI);
    ctx.globalAlpha = 1;
    ctx.fillStyle = skin == MapSkin.Light ? "#A1A1A1" : "#FFF";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 0.61, 0, 2 * Math.PI);
    ctx.fillStyle = "#FFF";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 0.44, 0, 2 * Math.PI);
    ctx.fillStyle = mainColor;
    ctx.fill();
    ctx.closePath();
  }

  public getOffscreenCanvas() {
    return this.offscreenCanvas;
  }

  public getItemCoordOnCanvas(powerId: number, mapZoom: number, skin: MapSkin): [number, number, number, number] {
    if (skin == MapSkin.Light) {
      powerId += 3;
    }

    let sizeIdx = this.getSizeIdxFromMapZoom(mapZoom);

    const xCoord = (powerId - 1) * CHARGER_GROUP_SIZES[sizeIdx];
    const yCoord = sizeIdx > 0 ? this.getArraySum(CHARGER_GROUP_SIZES.slice(0, sizeIdx)) : 0;
    return [xCoord, yCoord, CHARGER_GROUP_SIZES[sizeIdx], CHARGER_GROUP_SIZES[sizeIdx]];
  }

  public getCanvasChargerSize(mapZoom: number): number {
    return CHARGER_SIZES[this.getSizeIdxFromMapZoom(mapZoom)];
  }

  public getCanvasChargerGroupSize(mapZoom: number): number{
    return CHARGER_GROUP_SIZES[this.getSizeIdxFromMapZoom(mapZoom)];
  }

  private getSizeIdxFromMapZoom(mapZoom: number){
    if (mapZoom < 14) {
      return 0
    }
    else if (mapZoom < 15) {
      return 1;
    }
    else if (mapZoom < 17) {
      return 2;
    }
    else {
     return 3;
    }
  }

  public getIncidentIcon(incident: Incident): { iconHtml: string, tooltip: string } {
    let iconName = '';
    let tooltip = '';
    let backgroundIcon = '';

    // Determine the incident type icon
    switch (incident.Type) {
      case 'accident':
        iconName = 'accident.svg';
        tooltip = 'Accident';
        break;
      case 'construction':
        iconName = 'construction.svg';
        tooltip = 'Construction Work';
        break;
      case 'congestion':
        iconName = 'congestion.svg';
        tooltip = 'Congestion';
        break;
      case 'disabledVehicle':
        iconName = 'disabled_vehicle.svg';
        tooltip = 'Disabled Vehicle';
        break;
      case 'massTransit':
        iconName = 'mass_transit.svg';
        tooltip = 'Mass Transit Disruption';
        break;
      case 'plannedEvent':
        iconName = 'planned_event.svg';
        tooltip = 'Planned Event';
        break;
      case 'roadHazard':
        iconName = 'road_hazard.svg';
        tooltip = 'Road Hazard';
        break;
      case 'roadClosure':
        iconName = 'road_closure.svg';
        tooltip = 'Road Closure';
        break;
      case 'weather':
        iconName = 'weather_disruption.svg';
        tooltip = 'Weather Disruption';
        break;
      case 'laneRestriction':
        iconName = 'lane_restriction.svg';
        tooltip = 'Lane Restriction';
        break;
      case 'other':
        iconName = 'other_incident.svg';
        tooltip = 'Other Incident';
        break;
      default:
        iconName = 'unknown.svg';
        tooltip = 'Unknown Incident';
        break;
    }

    // Determine the background icon based on severity
    switch (incident.Severity) {
      case 'critical':
        backgroundIcon = 'incidents_icon_background_critical.svg';
        break;
      case 'major':
        backgroundIcon = 'incidents_icon_background_major.svg';
        break;
      case 'minor':
        backgroundIcon = 'incidents_icon_background_minor.svg';
        break;
      default:
        backgroundIcon = 'incidents_icon_background_minor.svg'; // default to minor if unknown
        break;
    }

    return {
      iconHtml: `
              <div class='incident-icon' style='position: relative; width: 24px; height: 24px;'>
                  <img src='${INCIDENT_ICON_BASE}/${backgroundIcon}' style='position: absolute; top: 0; left: 0; width: 24px; height: 24px;' />
                  <img src='${INCIDENT_ICON_BASE}/${iconName}' style='position: absolute; top: 0; left: 0; width: 24px; height: 24px;' alt='${tooltip}' />
              </div>
          `,
      tooltip
    };
  }

  public getRoadSurfaceAlertIcon(alert: RoadSurfaceAlert): { iconHtml: string, tooltip: string } {
    let iconName = '';
    let tooltip = '';
    let backgroundIcon = '';

    // Determine the alert type icon
    switch (alert.Type) {
      case 'roughness':
        iconName = 'roughness.svg';
        tooltip = 'Road Surface Roughness';
        break;
      case 'hydroplaning':
        iconName = 'hydroplaning.svg';
        tooltip = 'Hydroplaning';
        break;
      case 'pothole':
        iconName = 'road_damage.svg';
        tooltip = 'Pothole';
        break;
      case 'speedbump':
          iconName = 'road_damage.svg';
          tooltip = 'Speed Bump';
          break;
      case 'slippery':
        iconName = 'slipperiness.svg';
        tooltip = 'Slipperiness';
        break;
      default:
        iconName = 'roughness.svg';
        tooltip = 'Unknown Alert';
        break;
    }

    // Determine the background icon based on severity
    if (alert.Severity >= 0.75) {
      backgroundIcon = 'icon_background_critical.svg';
    } else if (alert.Severity >= 0.5) {
      backgroundIcon = 'icon_background_major.svg';
    } else {
      backgroundIcon = 'icon_background_minor.svg';
    }

    return {
      iconHtml: `
              <div class='alert-icon' style='position: relative; width: 24px; height: 24px;'>
                  <img src='${ROAD_SURFACE_ALERT_ICON_BASE}/${backgroundIcon}' style='position: absolute; top: 0; left: 0; width: 24px; height: 24px;' />
                  <img src='${ROAD_SURFACE_ALERT_ICON_BASE}/${iconName}' style='position: absolute; top: 0; left: 0; width: 24px; height: 24px;' alt='${tooltip}' />
              </div>
          `,
      tooltip
    };
  }
}
